import { AccountListingType, Box, Pagination, withStyles } from 'vopay';
import { ReactNode } from 'react';
import { Skeleton, TableFiltersContainer } from '#shared/components/UI';
import AccountDetailsDrawer from './AccountDetailsDrawer/AccountDetailsDrawer';
import AccountSearchForm from './AccountSearchForm/AccountSearchForm';
import PaymentMethodsTable from './PaymentMethodsTable/PaymentMethodsTable';
import { getAccountRowToken } from './buildAccountRows';
import { AccountListing } from './useAccountListing';
import { AccountRow } from './types';
import styles from './AccountListingPanel.module.scss';

const cn = withStyles(styles);

// The rendering half of an account listing: filter bar, table, pagination and the details drawer the
// table opens. Everything it draws comes from useAccountListing; what differs between the two
// listings is props.

export type AccountListingPanelProps = {
  listing: AccountListing;
  /** Which listing this is — the drawer shows both defaults but labels the one it was opened from. */
  accountListingType: AccountListingType;
  /** Gates Set as Default in the table and the drawer alike; which host permission that is is the host's call. */
  canSetDefault: boolean;
  /** Gates Delete Account in the drawer; which host permission that is is the host's call. */
  canDelete: boolean;
  /** Billing lets credit cards be set as default; Operational doesn't. */
  canSetDefaultCreditCards: boolean;
  setDefaultLabel: string;
  /** Whether this row is the current default for the listing being shown. */
  isDefaultRow: (account: AccountRow) => boolean;
  onSetDefault: (account: AccountRow) => void;
  /** The drawer deleted the account it was showing — refetch. */
  onAccountDeleted: () => void;
  /** Host controls in the details drawer (Operational's Add Funds / Withdraw). */
  renderDetailsActions?: (opts: { disabled: boolean }) => ReactNode;
  /**
   * The rows are still on their way. Renders a skeleton in place of the table — this is page data, so
   * per loading-conventions it never raises the fullscreen blocking loader.
   */
  isLoading?: boolean;
  /**
   * A refresh failed but the last good list is still on screen. Only a listing whose fetch exposes a
   * status can tell that apart from `loadError`, so both are optional.
   */
  refreshError?: string;
  /** Nothing could be loaded — replaces the table. */
  loadError?: string;
};

export default function AccountListingPanel({
  listing,
  accountListingType,
  canSetDefault,
  canDelete,
  canSetDefaultCreditCards,
  setDefaultLabel,
  isDefaultRow,
  onSetDefault,
  onAccountDeleted,
  renderDetailsActions,
  isLoading,
  refreshError,
  loadError,
}: AccountListingPanelProps) {
  const {
    queryParams,
    filtersApplied,
    submitSearch,
    pagedAccounts,
    pageCount,
    pageIndex,
    setPageIndex,
    selectedAccount,
    openAccountDetails,
    closeAccountDetails,
  } = listing;

  return (
    <>
      <Box flx="col" gap="sm">
        <TableFiltersContainer filtersApplied={filtersApplied}>
          <div className={cn('filtersTrigger')}>
            <TableFiltersContainer.Trigger />
          </div>

          <TableFiltersContainer.Content>
            <AccountSearchForm searchParams={queryParams} onSearch={submitSearch} />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
      </Box>

      {refreshError && <div className={cn('notice')}>{refreshError}</div>}
      {renderRows()}

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              setPageIndex(e.selected);
            }}
          />
        )}
      </Box>

      <AccountDetailsDrawer
        isOpen={selectedAccount !== null}
        onClose={closeAccountDetails}
        account={selectedAccount}
        accountListingType={accountListingType}
        canSetDefault={canSetDefault}
        canDelete={canDelete}
        renderActions={renderDetailsActions}
        onSetAsDefault={() => {
          if (selectedAccount) onSetDefault(selectedAccount);
        }}
        onDeleted={onAccountDeleted}
      />
    </>
  );

  function renderRows() {
    // Order matters: a failed first load has nothing to show, so it wins over the skeleton, which in
    // turn wins over an empty table that would read as "you have no payment methods".
    if (loadError) return <div className={cn('notice', 'noticeLarge')}>{loadError}</div>;

    if (isLoading) return <Skeleton height="320px" />;

    return (
      <PaymentMethodsTable
        accounts={pagedAccounts}
        isDefaultRow={isDefaultRow}
        canSetDefaultCreditCards={canSetDefaultCreditCards}
        isSetDefaultDisabled={!canSetDefault}
        setDefaultLabel={setDefaultLabel}
        onSetDefault={onSetDefault}
        onOpenDetails={(account) => openAccountDetails(getAccountRowToken(account))}
      />
    );
  }
}
